import React, { useState } from 'react';

const ITEM_LABELS = {
  cremation: '🌊 Cremation',
  keepsake:      '🐾 Keepsake',
  urn:           '🏺 Urn',
  transportation:'🚗 Transport',
  certificate:   '📜 Certificate',
  viewing:       '🕯️ Viewing Room',
};

const QBInvoicePanel = ({ petId, petEmail, onCreated, existingInvoiceId, existingTotal, existingEmailSent, vetStaffBenefit, hasUrnSelected }) => {
  const [preview, setPreview]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError]       = useState('');
  const [confirmation, setConfirmation] = useState(null); // { invoiceId, total, emailSent }

  const API = process.env.REACT_APP_BACKEND_URL;

  const loadPreview = async () => {
    setLoading(true); setError(''); setConfirmation(null);
    try {
      const res = await fetch(`${API}/api/quickbooks/invoices/preview/${petId}`);
      const d = await res.json();
      if (res.ok) setPreview(d);
      else setError(d.detail || 'Failed to load preview');
    } catch { setError('Failed to connect'); }
    finally { setLoading(false); }
  };

  const createInvoice = async (sendEmail) => {
    setCreating(true); setError('');
    try {
      const res = await fetch(`${API}/api/quickbooks/invoices/from-pet/${petId}?send_email=${sendEmail}`, { method: 'POST' });
      const d = await res.json();
      if (d.success) {
        setPreview(null);
        setConfirmation({ invoiceId: d.invoice_id, total: d.total, emailSent: sendEmail && d.email_sent });
        onCreated?.();
      } else {
        setError(d.detail || 'Failed to create invoice');
      }
    } catch { setError('Failed to connect'); }
    finally { setCreating(false); }
  };

  // ── Already has invoice ───────────────────────────────────────────────────
  if (existingInvoiceId && !confirmation) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">✅ Invoice created</span>
          {existingEmailSent && <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">📧 Email sent</span>}
        </div>
        <p className="text-sm text-gray-700">Total: <strong className="text-green-700">${existingTotal}</strong></p>
        <button onClick={loadPreview} disabled={loading}
          className="text-xs text-blue-600 hover:underline">
          {loading ? 'Loading...' : '+ Preview & create another invoice'}
        </button>
        {preview && <PreviewCard preview={preview} petEmail={petEmail} creating={creating}
          onConfirm={createInvoice} onCancel={() => setPreview(null)}
          vetStaffBenefit={vetStaffBenefit} hasUrnSelected={hasUrnSelected} />}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  // ── Confirmation banner ───────────────────────────────────────────────────
  if (confirmation) {
    return (
      <div className="space-y-3">
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-4">
          <p className="font-semibold text-green-800 text-sm mb-1">
            ✅ Invoice created successfully
          </p>
          <p className="text-sm text-green-700">
            Total: <strong>${confirmation.total?.toFixed ? confirmation.total.toFixed(2) : confirmation.total}</strong>
          </p>
          {confirmation.emailSent ? (
            <p className="text-sm text-teal-700 mt-1">📧 Email sent to client</p>
          ) : (
            <p className="text-sm text-gray-500 mt-1">📋 Invoice saved in QuickBooks — not emailed yet</p>
          )}
          <p className="text-xs text-gray-400 mt-2">QB Invoice ID: {confirmation.invoiceId}</p>
        </div>
        <button onClick={loadPreview} disabled={loading}
          className="text-xs text-blue-600 hover:underline">
          {loading ? 'Loading...' : '+ Preview & create another invoice'}
        </button>
        {preview && <PreviewCard preview={preview} petEmail={petEmail} creating={creating}
          onConfirm={createInvoice} onCancel={() => { setPreview(null); }}
          vetStaffBenefit={vetStaffBenefit} hasUrnSelected={hasUrnSelected} />}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  // ── No invoice yet ────────────────────────────────────────────────────────
  if (preview) {
    return (
      <div>
        <PreviewCard preview={preview} petEmail={petEmail} creating={creating}
          onConfirm={createInvoice} onCancel={() => setPreview(null)}
          vetStaffBenefit={vetStaffBenefit} hasUrnSelected={hasUrnSelected} />
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {vetStaffBenefit === 'complimentary_urn' && !hasUrnSelected && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2" data-testid="comp-urn-invoice-warning">
          <p className="text-sm font-semibold text-purple-800">
            <i className="fa-solid fa-triangle-exclamation mr-1"></i>
            Complimentary urn not yet selected — select an urn before invoicing
          </p>
        </div>
      )}
      <p className="text-sm text-gray-500">No invoice created yet.</p>
      <button onClick={loadPreview} disabled={loading}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-semibold px-3 py-2.5 rounded-lg transition-colors">
        {loading ? '⏳ Loading preview...' : '👁️ Preview Invoice'}
      </button>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};


const PreviewCard = ({ preview, petEmail, creating, onConfirm, onCancel, vetStaffBenefit, hasUrnSelected }) => {
  const { customer, pet_name, lines = [], total, gst_included, has_email, email } = preview;
  const showCompUrnWarning = vetStaffBenefit === 'complimentary_urn' && !hasUrnSelected;

  return (
    <div className="border border-blue-200 rounded-xl overflow-hidden mt-2">
      {/* Complimentary urn warning in preview */}
      {showCompUrnWarning && (
        <div className="bg-purple-600 text-white px-4 py-2.5 flex items-center gap-2" data-testid="comp-urn-preview-warning">
          <i className="fa-solid fa-triangle-exclamation"></i>
          <span className="text-sm font-semibold">No urn selected — complimentary urn will not appear on this invoice</span>
        </div>
      )}
      {vetStaffBenefit === 'complimentary_urn' && hasUrnSelected && (
        <div className="bg-purple-50 text-purple-800 px-4 py-2 text-sm border-b border-purple-200">
          <i className="fa-solid fa-check-circle mr-1 text-purple-600"></i>
          Complimentary urn will show as <strong>$0.00</strong> on this invoice
        </div>
      )}
      {/* Header */}
      <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
        <p className="font-semibold text-blue-900 text-sm">📄 Invoice Preview</p>
        <p className="text-xs text-blue-600 mt-0.5">
          {customer} — <span className="font-medium">{pet_name}</span>
          {email && <span className="ml-1 text-blue-400">· {email}</span>}
        </p>
      </div>

      {/* Line items */}
      <div className="divide-y divide-gray-100">
        {lines.map((l, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
            <div>
              <span className="text-gray-500 text-xs mr-1.5">{ITEM_LABELS[l.item_key] || l.item_key}</span>
              <span className="text-gray-800">{l.description}</span>
            </div>
            <span className="font-semibold text-gray-900 ml-4 flex-shrink-0">
              ${l.unit_price.toFixed(2)}
            </span>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Includes GST (10%)</span>
          <span className="text-gray-600">${gst_included?.toFixed(2)}</span>
        </div>
        <div className="flex justify-between font-bold text-base">
          <span>Total (incl GST)</span>
          <span className="text-green-700">${total?.toFixed(2)}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="px-4 py-3 bg-white border-t border-gray-100 space-y-2">
        <div className="flex gap-2">
          <button onClick={() => onConfirm(false)} disabled={creating}
            className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white text-sm font-semibold py-2 rounded-lg transition-colors">
            {creating ? '⏳ Creating...' : '📄 Create Invoice'}
          </button>
          <button onClick={() => onConfirm(true)} disabled={creating || !has_email}
            title={!has_email ? 'No email address on record' : ''}
            className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold py-2 rounded-lg transition-colors">
            {creating ? '⏳ Creating...' : '📧 Create & Send'}
          </button>
        </div>
        {!has_email && (
          <p className="text-xs text-amber-600 text-center">⚠️ No email on record — cannot send directly</p>
        )}
        <button onClick={onCancel} disabled={creating}
          className="w-full text-sm font-bold text-black hover:text-gray-700 py-1">
          Cancel
        </button>
      </div>
    </div>
  );
};


export default QBInvoicePanel;
